import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/lib/brand-config';

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function recentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Mobile install nudge for the app.
 *
 * - Android / Chrome: shows an "Install" button wired to the native prompt
 *   captured by usePwaInstall (only rendered once that prompt is available).
 * - iOS Safari: no programmatic prompt exists, so it shows the manual
 *   "Share → Add to Home Screen" steps instead.
 *
 * Hidden on desktop, when already installed (standalone), and for 14 days after
 * the user dismisses it. Mounted only on the app domain (not marketing).
 */
export function InstallPrompt() {
  const isMobile = useIsMobile();
  const { isStandalone, isIos, canPrompt, promptInstall } = usePwaInstall();
  // Start dismissed so nothing flashes before the localStorage check runs.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(recentlyDismissed());
  }, []);

  if (dismissed || isStandalone || !isMobile) return null;
  // Android needs the captured native prompt; iOS falls back to instructions.
  if (!canPrompt && !isIos) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Private mode / storage disabled — just hide for this session.
    }
    setDismissed(true);
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    // Whether accepted or dismissed, don't nag again for a while.
    if (outcome !== 'unavailable') dismiss();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-lg">
        <img
          src="/pwa-192x192.png"
          alt=""
          className="h-11 w-11 shrink-0 rounded-lg"
          width={44}
          height={44}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Install {BRAND.pwaShortName}</p>
          {isIos ? (
            <p className="text-xs text-muted-foreground">
              Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" aria-label="Share" /> then
              &ldquo;Add to Home Screen&rdquo;
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Add it to your home screen for one-tap access.
            </p>
          )}
        </div>
        {!isIos && (
          <Button
            size="sm"
            onClick={handleInstall}
            className="min-h-[44px] shrink-0"
          >
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Install
          </Button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
